import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Typography,
  Divider,
  message,
  Alert,
  Spin,
  Space,
  Checkbox
} from 'antd';
import { app } from "../../../service/serviceLMS";
import dayjs from 'dayjs';

const { Text } = Typography;

const FrequencyModal = ({ enrollments, onCancel }) => {
  const [groupedEnrollments, setGroupedEnrollments] = useState({});
  const [groupFrequencies, setGroupFrequencies] = useState({});
  const [groupCycles, setGroupCycles] = useState({});
  const [metaMap, setMetaMap] = useState({});
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [showDetailsKey, setShowDetailsKey] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [useSameFrequency, setUseSameFrequency] = useState(false);
  const [sameFrequencyValue, setSameFrequencyValue] = useState(1);
  const [sameCycleValue, setSameCycleValue] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getGroupKey = (e) => {
    if (e.bundleId && e.groupId) return `bundle-${e.bundleId}-group-${e.groupId}`;
    if (e.bundleId && e.userId) return `bundle-${e.bundleId}-user-${e.userId}`;
    if (e.courseId && e.groupId) return `course-${e.courseId}-group-${e.groupId}`;
    if (e.courseId && e.userId) return `course-${e.courseId}-user-${e.userId}`;
    return `enrollment-${e.enrollmentId}`;
  };

  useEffect(() => {
    const grouped = {};
    enrollments.forEach(e => {
      const key = getGroupKey(e);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(e);
    });
    setGroupedEnrollments(grouped);
  }, [enrollments]);

  useEffect(() => {
    const fetchMeta = async () => {
      setLoadingMeta(true);
      const map = {};

      for (const e of enrollments) {
        const { courseId, bundleId, userId, groupId } = e;

        if (courseId && !map[courseId]) {
          const res = await app.get(`course/api/client-api/course/${courseId}`).catch(() => null);
          map[courseId] = res?.data?.data?.title || `Course ID ${courseId}`;
        }

        if (bundleId && !map[bundleId]) {
          const res = await app.get(`course/api/client-api/bundles/${bundleId}`).catch(() => null);
          map[bundleId] = res?.data?.data?.bundleName || `Bundle ID ${bundleId}`;
        }

        if (userId && !map[`user-${userId}`]) {
          const res = await app.get(`user/api/client-api/users/${userId}`).catch(() => null);
          const user = res?.data?.data;
          map[`user-${userId}`] =
            user?.firstName || user?.lastName
              ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
              : user?.username || `User ID ${userId}`;
        }

        if (groupId && !map[`group-${groupId}`]) {
          const res = await app.get(`user/api/client-api/group/${groupId}`).catch(() => null);
          map[`group-${groupId}`] = res?.data?.data?.groupName || `Group ID ${groupId}`;
        }
      }

      setMetaMap(map);
      setLoadingMeta(false);
    };

    fetchMeta();
  }, [enrollments]);

  const getDisplayName = (e) => {
    if (e.bundleId) return metaMap[e.bundleId] || `Bundle ID ${e.bundleId}`;
    if (e.courseId) return metaMap[e.courseId] || `Course ID ${e.courseId}`;
    return 'Unknown Content';
  };

  const getAssignedTo = (e) => {
    if (e.groupId) return `Group - ${metaMap[`group-${e.groupId}`] || `Group ID ${e.groupId}`}`;
    if (e.userId) return `User - ${metaMap[`user-${e.userId}`] || `User ID ${e.userId}`}`;
    return 'Unassigned';
  };

  const getCoursesInBundle = (bundleId) => {
    const courseIds = enrollments
      .filter(e => e.bundleId === bundleId)
      .map(e => e.courseId);
    const uniqueCourseIds = [...new Set(courseIds)];
    return uniqueCourseIds.map(id => ({
      courseId: id,
      title: metaMap[id] || `Course ID ${id}`
    }));
  };

  const getMembersInGroup = (groupId) => {
    const userIds = enrollments
      .filter(e => e.groupId === groupId)
      .map(e => e.userId);
    const uniqueUserIds = [...new Set(userIds)];
    return uniqueUserIds.map(id => ({
      userId: id,
      name: metaMap[`user-${id}`] || `User ID ${id}`
    }));
  };

  const handleShowDetails = (key, sampleEnrollment) => {
    const details = {};

    if (sampleEnrollment.bundleId) {
      details.courses = getCoursesInBundle(sampleEnrollment.bundleId);
    }

    if (sampleEnrollment.groupId) {
      details.members = getMembersInGroup(sampleEnrollment.groupId);
    }

    details.bundleId = sampleEnrollment.bundleId || null;
    details.groupId = sampleEnrollment.groupId || null;

    setDetailsData(details);
    setShowDetailsKey(key);
  };

  const handleSubmit = async () => {
    const frequencies = [];

    Object.entries(groupedEnrollments).forEach(([key, group]) => {
      const freq = useSameFrequency ? sameFrequencyValue : groupFrequencies[key] || 1;
      const cycles = useSameFrequency ? sameCycleValue : groupCycles[key] || null;

      group.forEach(e => {
        frequencies.push({
          enrollmentId: e.enrollmentId,
          frequencyInMonths: freq,
          numberOfCycles: cycles,
          userId: e.userId || null,
          groupId: e.groupId || null,
          courseId: e.courseId || null,
          bundleId: e.bundleId || null

        });
      });
    });

    try {
      const response = await app.post('user/api/client-api/enrollment/setFrequency', { frequencies });
      if (response?.data?.status === 'SUCCESS') {
        message.success('Frequencies saved successfully');
        onCancel();
      } else {
        message.error(response?.data?.message || 'Failed to save frequencies');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Error saving frequencies');
    }
  };


return (
  <>
    <Modal
      title="Set Enrollment Frequency"
      visible={true}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Alert
        message="Set Frequency and Duration"
        description="Set how often each assignment should repeat and for how many cycles."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {loadingMeta ? (
        <Spin tip="Loading enrollment details..." />
      ) : (
        <>
          {useSameFrequency ? (
            <>
              <Form.Item label="Frequency (in months)">
                <InputNumber
                  min={1}
                  value={sameFrequencyValue}
                  onChange={setSameFrequencyValue}
                />
              </Form.Item>
              <Form.Item label="Number of cycles (optional)">
                <Space.Compact style={{ display: 'flex' }}>
                  <InputNumber
                    min={1}
                    value={sameCycleValue}
                    placeholder="Leave blank for ongoing"
                    onChange={value => setSameCycleValue(value || null)}
                    style={{ flex: 1 }}
                  />
                  <Button onClick={() => setSameCycleValue(null)}>Clear</Button>
                </Space.Compact>
              </Form.Item>
              
              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <strong>Schedule Preview (applied to all enrollments):</strong>
                  <br />
                  Next Enrollment: {dayjs().add(sameFrequencyValue, 'month').format('DD MMM YYYY')}
                  {sameCycleValue && sameCycleValue > 1 && (
                    <>
                      <br />
                      Continues Until: {dayjs().add(sameFrequencyValue * sameCycleValue, 'month').format('DD MMM YYYY')}
                      <br />
                      Total Enrollments: {sameCycleValue + 1} (initial + {sameCycleValue} repeats)
                    </>
                  )}
                  {!sameCycleValue && (
                    <>
                      <br />
                      Continues Until: Ongoing (no cycle limit)
                    </>
                  )}
                  <br />
                </Text>
              </div>
            </>
          ) : (
            <>
              {Object.entries(groupedEnrollments).map(([key, group], index) => {
                if (index !== currentIndex) return null;
                const sample = group[0];
                const freq = groupFrequencies[key] || 1;
                const cycles = groupCycles[key] || null;
                const assignedAt = sample.assignedAt ? dayjs(sample.assignedAt) : dayjs();

                return (
                  <div key={key} style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong>
                          {sample.bundleId ? 'Bundle' : 'Course'}: {getDisplayName(sample)}
                        </Text>
                        <br />
                        <Text type="secondary">Assigned To: {getAssignedTo(sample)}</Text>
                      </div>

                      {(sample.bundleId || sample.groupId) && (
                        <Button type="link" onClick={() => handleShowDetails(key, sample)}>
                          View Details
                        </Button>
                      )}
                    </div>

                    <Form.Item label="Frequency (in months)">
                      <InputNumber
                        min={1}
                        value={freq}
                        onChange={value =>
                          setGroupFrequencies(prev => ({ ...prev, [key]: value || 1 }))
                        }
                      />
                    </Form.Item>
                    <Form.Item label="Number of cycles (optional)">
                      <Space.Compact style={{ display: 'flex' }}>
                        <InputNumber
                          min={1}
                          value={cycles}
                          placeholder="Leave blank for ongoing"
                          onChange={value =>
                            setGroupCycles(prev => ({
                              ...prev,
                              [key]: value || null
                            }))
                          }
                          style={{ flex: 1 }}
                        />
                        <Button onClick={() => setGroupCycles(prev => ({ ...prev, [key]: null }))}>Clear</Button>
                      </Space.Compact>
                    </Form.Item>

                    <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <strong>Schedule Preview:</strong>
                        <br />
                        Next Enrollment: {assignedAt.add(freq, 'month').format('DD MMM YYYY')}
                        {cycles && cycles > 1 && (
                          <>
                            <br />
                            Continues Until: {assignedAt.add(freq * cycles, 'month').format('DD MMM YYYY')}
                            <br />
                            Total Enrollments: {cycles + 1} (initial + {cycles} repeats)
                          </>
                        )}
                        {!cycles && (
                          <>
                            <br />
                            Continues Until: Ongoing (no cycle limit)
                          </>
                        )}
                        <br />
                      </Text>
                    </div>
                  </div>
                );
              })}

              <Space style={{ marginBottom: 16 }}>
                <Button
                  disabled={currentIndex === 0}
                  onClick={() => setCurrentIndex(i => i - 1)}
                >
                  Previous
                </Button>
                <Button
                  disabled={currentIndex === Object.keys(groupedEnrollments).length - 1}
                  onClick={() => setCurrentIndex(i => i + 1)}
                >
                  Next
                </Button>
                <Text type="secondary">
                  {`${currentIndex + 1} of ${Object.keys(groupedEnrollments).length}`}
                </Text>
              </Space>
            </>
          )}

          <Divider />

          {Object.entries(groupedEnrollments).length > 1 && (
            <Checkbox
              checked={useSameFrequency}
              onChange={e => setUseSameFrequency(e.target.checked)}
              style={{ marginBottom: 16 }}
            >
              Apply same settings to all
            </Checkbox>
          )}

          {useSameFrequency && (
            <Alert
              message="All enrollments will follow the same schedule pattern."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}>
              Save Frequency
            </Button>
          </Space>
        </>
      )}
    </Modal>

    {showDetailsKey && detailsData && (
      <Modal
        title="Assignment Details"
        visible={true}
        onCancel={() => setShowDetailsKey(null)}
        footer={null}
        destroyOnClose
      >
        {detailsData.courses && (
          <>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              📦 Courses in Bundle:
            </Text>
            <ul style={{ marginBottom: 24 }}>
              {detailsData.courses.map(c => (
                <li key={c.courseId}>{c.title}</li>
              ))}
            </ul>
          </>
        )}

        {detailsData.members && (
          <>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              👥 Group Members:
            </Text>
            <ul>
              {detailsData.members.map(m => (
                <li key={m.userId}>{m.name}</li>
              ))}
            </ul>
          </>
        )}
      </Modal>
    )}
  </>
);
};

export default FrequencyModal;
